import React from 'react';
import { Box, Typography, Button, TablePagination } from '@mui/material';
import BondTable from '../BondTable';
import { tokens } from '../../Common/designTokens';

// Column definitions for User Config
const writeAccessDeniedMessage = 'Only SUPER_ADMIN can modify session and user configuration.';

const getUserColumns = (onEditClick, onDeleteClick, canWrite = false) => [
  {
    key: 'venueUser',
    name: 'Venue User',
    width: 150,
  },
  {
    key: 'venueName',
    name: 'Venue Name',
    width: 150,
  },
  {
    key: 'userName',
    name: 'User Name',
    width: 150,
  },
  {
    key: 'customerName',
    name: 'Customer Name',
    width: 150,
  },
  {
    key: 'action_user_config',
    name: 'Action',
    width: 194,
    minWidth: 167,
    sortable: false,
    render: (value, rowData) => (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
        <Box component="span" title={!canWrite ? writeAccessDeniedMessage : undefined}>
          <Button
            size="small"
            variant="outlined"
            disabled={!canWrite}
            onClick={(e) => {
              e.stopPropagation();
              if (!canWrite) return;
              if (onEditClick) onEditClick(rowData);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: tokens.palette.primary,
              borderColor: tokens.palette.primary,
              '&:hover': {
                borderColor: tokens.palette.primary,
                backgroundColor: tokens.colors.defaultGridHoverColor,
              },
              '&.Mui-disabled': {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
                opacity: 0.7,
              },
            }}
          >
            Update
          </Button>
        </Box>
        <Box component="span" title={!canWrite ? writeAccessDeniedMessage : undefined}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!canWrite}
            onClick={(e) => {
              e.stopPropagation();
              if (!canWrite) return;
              if (onDeleteClick) onDeleteClick(rowData);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: tokens.palette.danger,
              color: tokens.palette.danger,
              '&:hover': {
                borderColor: tokens.palette.danger,
                backgroundColor: tokens.colors.defaultGridHoverColor,
              },
              '&.Mui-disabled': {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
                opacity: 0.7,
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    ),
  },
];

const UserConfigTable = ({
  data,
  setData,
  sortConfig,
  onSort,
  error,
  loading,
  onEditClick,
  onDeleteClick,
  page,
  rowsPerPage,
  totalElements,
  onPageChange,
  onRowsPerPageChange,
  canWrite = false,
}) => {
  const columns = getUserColumns(onEditClick, onDeleteClick, canWrite);

  // Show error message if there's an error
  if (error && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '400px',
        }}
      >
        <Typography
          sx={{
            color: tokens.palette.danger,
            fontSize: '16px',
            fontWeight: 500,
            marginBottom: '8px',
          }}
        >
          Error Loading User Configuration
        </Typography>
        <Typography
          sx={{
            color: tokens.colors.fontColour50,
            fontSize: '14px',
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  // Show empty message if no data and not loading
  if (!loading && data.length === 0 && !error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: '400px',
        }}
      >
        <Typography
          sx={{
            color: tokens.colors.fontColour50,
            fontSize: '16px',
          }}
        >
          No user configuration data available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ padding: "0px", height: "calc(100vh - 225px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <BondTable
        source="userConfig"
        selectedColumns={columns}
        filteredData={data}
        handleRowClick={() => {}} // No row click action
        handleViewJson={() => {}} // No view JSON action
        handleBuyClick={() => {}} // No buy action
        handleCusipClick={() => {}} // No CUSIP click action
        handleAllocateAccount={() => {}} // No allocate action
        handleEditClick={onEditClick} // Pass edit handler
        onOrderAdded={() => {}}
        orderData={data}
        setOrderData={setData}
        sectionKey="userConfig"
        onSort={onSort}
        sortConfig={sortConfig}
      />
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          color: "white",
          zIndex: 1000,
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <TablePagination
          component="footer"
          sx={{
            ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          page={page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          count={totalElements}
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
        />
      </Box>
    </>
  );
};

export default UserConfigTable;
